import { useForm } from 'react-hook-form';
import { useRouter } from '@/hooks/general/useRouter/useRouter';
import { useStatesQuery } from '@/hooks/queries/dictionary/dictionaryQueries';
import { useServerErrors } from '@/hooks/forms/useServerErrors';
import { useZipLocations } from '@/hooks/forms/useZipLocations';
import { useQueryParams } from '@/hooks/general/useQueryParams';
import { Grid } from '@mui/material';
import { RegistrationStep } from '@/types/enums/forms/registration/registrationWizard';
import { registerFormModel } from '@/components/forms/forms/registration/registerFormModel';
import { InputFieldSuggestions, SelectField, TextField } from '@/components/forms/inputs';
import { PreviousNextButtons } from '@/components/forms/other/PreviousNextButtons/PreviousNextButtons';
import { createSelectOptionsFromArray } from '@/utils/forms';
import { useRegistrationStore } from '@/store/pages/registrationStore/registrationStore';
import { getDefaultValues, submitAddressData } from './addressFormUtils';
import { FORM_STEP_PHRASES } from '@/consts/phrases/registrationPhrases';
import { useAuthStore } from '@/store/authStore/authStore';
import {
    IAllRegistrationData,
    IRegistrationAddress,
} from '@/types/types/forms/registration/registration.interface';
import { IWalletPersonalData } from '@/types/types/forms/wallet/wallet.interface';
import { DefaultPageProps } from '@/types/types/page/pages';
import { FormGridErrors } from '@/components/forms/alerts/FormGridErrors/FormGridErrors';
import { isFeatureEnabled } from '@/config';

type AddressFormModel = (typeof registerFormModel)[RegistrationStep.Address];
type AddressFormFields = keyof AddressFormModel;
export type AddressFormValues = Record<AddressFormFields, string | number>;

interface AddressFormProps {
    registrationData?: IAllRegistrationData;
    nonMemberPersonalData?: Partial<IWalletPersonalData>;
    paymentToken?: string | undefined;
    dictionary?: DefaultPageProps['dictionary'];
    isYotiCompleted: boolean;
}

export const AddressForm = ({
    registrationData,
    nonMemberPersonalData,
    paymentToken,
    dictionary,
    isYotiCompleted,
}: AddressFormProps) => {
    const addressFormModel = registerFormModel[RegistrationStep.Address];
    const { address1, zip, city, stateCode, countryCode } = addressFormModel;

    const hasRegistrationCompleteFlow = isFeatureEnabled('registrationCompleteFlow');

    const values = getDefaultValues(registrationData, nonMemberPersonalData);

    const router = useRouter();
    const {
        goToPrevStep,
        reset: resetRegistrationStore,
        registrationId,
        setRegistrationId,
    } = useRegistrationStore((state) => ({
        goToPrevStep: state.goToPrevStep,
        reset: state.reset,
        registrationId: state.registrationId,
        setRegistrationId: state.setRegistrationId,
    }));

    const loginOnRegComplete = useAuthStore((state) => state.loginOnRegComplete);

    const {
        handleSubmit,
        control,
        formState,
        watch,
        getValues,
        resetField,
        setValue,
        setError,
        clearErrors,
    } = useForm<Partial<AddressFormValues>>({
        values,
    });

    const [zipValue, countryCodeValue] = watch(['zip', 'countryCode']) as [string, string];

    const {
        statesList: zipBasedStatesList,
        citiesList: zipBasedCitiesList,
        isMoreThanOneState: zipBasedIsMoreThanOneState,
        onZipChange,
    } = useZipLocations<Partial<AddressFormValues>>({
        zipValue,
        countryCodeValue,
        setValue,
    });

    const { errors, isSubmitting } = formState;

    const { hhid_uid: hhidUid } = useQueryParams();

    const { handleServerErrors, serverErrors } = useServerErrors({
        setError,
        clearErrors,
        getValues,
    });

    const selectedCountryCode = watch('countryCode');
    const states = useStatesQuery(
        (selectedCountryCode as string) ||
            (registrationData?.countryCode as string) ||
            nonMemberPersonalData?.countryCode,
    );
    const selectedCountryHasStates = Boolean(states?.length);

    const getStateSelectOptions = () => {
        if (!states?.length) {
            return [];
        } else {
            return createSelectOptionsFromArray({ label: 'name', value: 'stateCode' })(states);
        }
    };

    const getCountrySelectOptions = () =>
        createSelectOptionsFromArray({ label: 'phraseId', value: 'countryCode' })(
            dictionary?.countries?.filter((item) => item.isRegistrationOpen) || [],
        );

    const stateSelectOptions = getStateSelectOptions();
    const countrySelectOptions = getCountrySelectOptions();

    const onRegistrationComplete = async () => {
        if (!registrationId) return;
        const { isEmailConfirmed } = await loginOnRegComplete(
            registrationId,
            dictionary?.countries,
        );
        setRegistrationId(null);
        if (isEmailConfirmed && !hasRegistrationCompleteFlow) {
            router.push('/surveys');
        } else {
            router.push('/getconfirmed');
        }
    };

    const handleSubmitData = (vals: Partial<IRegistrationAddress>) => {
        const data = { ...vals, paymentToken, hhidUid: Number(hhidUid) };

        const resetStateCodeIfInvalid = () => {
            const availableStateCodes = states?.map((s) => s.stateCode);
            const hasValidStateCode = availableStateCodes?.includes(vals.stateCode ?? '');
            if (!hasValidStateCode) resetField('stateCode');
        };

        resetStateCodeIfInvalid();

        return submitAddressData(
            registrationId,
            data,
            onRegistrationComplete,
            resetRegistrationStore,
            handleServerErrors,
        );
    };

    return (
        <form onSubmit={handleSubmit(handleSubmitData)} noValidate>
            <Grid container spacing={2}>
                <FormGridErrors
                    errors={serverErrors}
                    gridSx={{ color: 'red', textAlign: 'center', mb: 2 }}
                    generalErrorId="message_box"
                />
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={address1.name}
                        label={address1.label}
                        type="text"
                        fullWidth
                        error={errors.address1}
                        required
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={zip.name}
                        label={zip.label}
                        type="text"
                        fullWidth
                        error={errors.zip}
                        onChange={onZipChange}
                        required
                    />
                </Grid>
                <Grid size={12}>
                    <TextField
                        control={control}
                        name={city.name}
                        label={city.label}
                        type="text"
                        fullWidth
                        error={errors.city}
                    />
                    <InputFieldSuggestions
                        name="city"
                        data={zipBasedCitiesList}
                        setValue={setValue}
                    />
                </Grid>
                {selectedCountryHasStates && (
                    <Grid size={12}>
                        <SelectField
                            control={control}
                            name={stateCode.name}
                            label={stateCode.label}
                            data={stateSelectOptions}
                            error={errors.stateCode}
                            fullWidth
                            required
                        />
                        {zipBasedIsMoreThanOneState && (
                            <InputFieldSuggestions
                                name="stateCode"
                                data={zipBasedStatesList}
                                setValue={setValue}
                            />
                        )}
                    </Grid>
                )}
                <Grid size={12}>
                    <SelectField
                        control={control}
                        name={countryCode.name}
                        label={countryCode.label}
                        data={countrySelectOptions}
                        error={errors.countryCode}
                        fullWidth
                        required
                        disabled
                    />
                </Grid>
            </Grid>
            <Grid
                container
                sx={{
                    justifyContent: 'flex-end',
                    marginTop: 2,
                }}
            >
                <PreviousNextButtons
                    nextLabel={FORM_STEP_PHRASES.SUBMIT}
                    nextDisabled={isSubmitting}
                    onPreviousClicked={goToPrevStep}
                    hidePrevious={isYotiCompleted}
                />
            </Grid>
        </form>
    );
};
